import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { CaptureRecord, ReportsPagination, ReportsSorting } from './types';
import { ReportsRowActions } from './ReportsRowActions';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ReportsTableProps {
  records: CaptureRecord[];
  pagination: ReportsPagination;
  sorting: ReportsSorting;
  onPaginationChange: (pagination: ReportsPagination) => void;
  onSortingChange: (sorting: ReportsSorting) => void;
  onExportRow: (record: CaptureRecord) => void;
  onDelete?: (record: CaptureRecord) => void;
}

export const ReportsTable: React.FC<ReportsTableProps> = ({
  records,
  pagination,
  sorting,
  onPaginationChange,
  onSortingChange,
  onExportRow,
  onDelete,
}) => {
  useLanguage();
  const handleSort = (field: ReportsSorting['field']) => {
    if (sorting.field === field) {
      onSortingChange({
        field,
        direction: sorting.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      onSortingChange({ field, direction: 'desc' });
    }
  };

  const SortIcon = ({ field }: { field: ReportsSorting['field'] }) => {
  useLanguage();
    if (sorting.field !== field) return <ArrowUpDown className="ml-2 h-4 w-4" />;
    return sorting.direction === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  const totalPages = Math.ceil(pagination.totalRecords / pagination.pageSize);
  const startRecord = (pagination.page - 1) * pagination.pageSize + 1;
  const endRecord = Math.min(pagination.page * pagination.pageSize, pagination.totalRecords);

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F9FAFB] border-b border-[#E5E7EB] hover:bg-[#F9FAFB]">
          {/* 1. Data de Captura */}
          <TableHead className="text-[#6B7280] font-semibold py-4 text-[13px]">
            <Button
              variant="ghost"
              onClick={() => handleSort('created_at')}
              className="h-8 px-2 text-[#6B7280] font-semibold hover:bg-transparent hover:text-[#6B7280]"
            >
              {translateInterfaceText("Data de Captura")}<SortIcon field="created_at" />
            </Button>
          </TableHead>
          
          {/* 2. Nome do Vídeo */}
          <TableHead className="text-[#6B7280] font-semibold py-4 text-[13px]">
            <Button
              variant="ghost"
              onClick={() => handleSort('video_name')}
              className="h-8 px-2 text-[#6B7280] font-semibold hover:bg-transparent hover:text-[#6B7280]"
            >
              {translateInterfaceText("Nome do Vídeo")}<SortIcon field="video_name" />
            </Button>
          </TableHead>
          
          {/* 3. Nome */}
          <TableHead className="text-[#6B7280] font-semibold py-4 text-[13px]">
            <Button
              variant="ghost"
              onClick={() => handleSort('name')}
              className="h-8 px-2 text-[#6B7280] font-semibold hover:bg-transparent hover:text-[#6B7280]"
            >
              {translateInterfaceText("Nome")}<SortIcon field="name" />
            </Button>
          </TableHead>
          
          {/* 4. E-mail */}
          <TableHead className="text-[#6B7280] font-semibold py-4 text-[13px]">
            <Button
              variant="ghost"
              onClick={() => handleSort('email')}
              className="h-8 px-2 text-[#6B7280] font-semibold hover:bg-transparent hover:text-[#6B7280]"
            >
              {translateInterfaceText("E-mail")}<SortIcon field="email" />
            </Button>
          </TableHead>
          
          {/* 5. Telefone/WhatsApp */}
          <TableHead className="text-[#6B7280] font-semibold py-4 text-[13px]">{translateInterfaceText("Telefone/WhatsApp")}</TableHead>
          
          {/* 6. Ações */}
          <TableHead className="text-[#6B7280] font-semibold py-4 text-[13px] text-right">{translateInterfaceText("Ações")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  {translateInterfaceText("Nenhum registro encontrado")}</TableCell>
              </TableRow>
            ) : (
              records.map((record, index) => (
                <TableRow 
                  key={record.id} 
                  className={`border-b border-[#E5E7EB] hover:bg-[#F3F4F6] hover:shadow-sm transition-all duration-200 ${
                    index % 2 === 1 ? 'bg-[#F9FAFB]' : ''
                  }`}
                >
                  {/* 1. Data de Captura */}
                  <TableCell>
                    {format(new Date(record.created_at_utc), 'dd/MM/yyyy HH:mm', {
                      locale: getInterfaceDateLocale(),
                    })}
                  </TableCell>

                  {/* 2. Nome do Vídeo */}
                  <TableCell className="max-w-[200px]">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="truncate inline-block max-w-[200px] text-sm text-[#1A1A1A] cursor-default">
                            {record.video_name}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{record.video_name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>

                  {/* 3. Nome */}
                  <TableCell className="font-medium max-w-[180px] truncate">
                    {record.name}
                  </TableCell>

                  {/* 4. E-mail */}
                  <TableCell className="max-w-[220px]">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="truncate inline-block max-w-[220px] text-sm text-[#1A1A1A] cursor-default">
                            {record.email || '-'}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{record.email || translateInterfaceText("Não informado")}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>

                  {/* 5. Telefone */}
                  <TableCell>{record.phone || '-'}</TableCell>

                  {/* 6. Ações */}
                  <TableCell className="text-right">
                    <ReportsRowActions
                      record={record}
                      onExportRow={onExportRow}
                      onDelete={onDelete}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{translateInterfaceText("Linhas por página:")}</span>
          <Select
            value={String(pagination.pageSize)}
            onValueChange={(value) =>
              onPaginationChange({ ...pagination, pageSize: Number(value) as any, page: 1 })
            }
          >
            <SelectTrigger className="w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            {startRecord}-{endRecord} {" "}{translateInterfaceText("de")}{" "}{pagination.totalRecords}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPaginationChange({ ...pagination, page: pagination.page - 1 })}
              disabled={pagination.page === 1}
            >
              {translateInterfaceText("Anterior")}</Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPaginationChange({ ...pagination, page: pagination.page + 1 })}
              disabled={pagination.page === totalPages}
            >
              {translateInterfaceText("Próximo")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};
